import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { toneClass, type Tone } from '../../../components/Tone';
import styles from './Scorecard.module.css';

export interface Choice<T extends string> {
  readonly value: T;
  readonly label: string;
  /** The status tone a chosen one fills with; without one it fills with the brand. */
  readonly tone?: Tone;
  /** Quieter ink while not chosen, for the way out of the scale. */
  readonly isQuiet?: boolean;
  readonly title?: string;
}

interface ChoiceGroupProps<T extends string> {
  readonly label: string;
  readonly choices: readonly Choice<T>[];
  readonly value: T | null;
  /** Null when the chosen one is pressed again — a score can be taken back. */
  readonly onChange: (value: T | null) => void;
  readonly disabled?: boolean;
  readonly isInvalid?: boolean;
  /** Wide buttons that share the row, for the final call. */
  readonly isWide?: boolean;
}

/**
 * A row of large pressable choices — the rubric's scale, "Not assessed", the final call.
 * Our own control, not stock radios (CLAUDE.md §5): 44px targets for a tablet in a meeting
 * room (D9), and arrow keys move along the row as a radio group's should (06-qol §2).
 */
export function ChoiceGroup<T extends string>({ label, choices, value, onChange, disabled = false, isInvalid = false, isWide = false }: ChoiceGroupProps<T>): ReactNode {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = choices.findIndex((choice) => choice.value === value);
  const focusable = selectedIndex === -1 ? 0 : selectedIndex;

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = (index + step + choices.length) % choices.length;
    const choice = choices[next];
    if (!choice) return;
    buttons.current[next]?.focus();
    onChange(choice.value);
  }

  return (
    <div role="radiogroup" aria-label={label} aria-invalid={isInvalid || undefined} className={`${styles.choices} ${isWide ? styles.choicesWide : ''} ${isInvalid ? styles.choicesInvalid : ''}`}>
      {choices.map((choice, index) => {
        const isSelected = choice.value === value;
        return (
          <button
            key={choice.value}
            ref={(element) => {
              buttons.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={index === focusable ? 0 : -1}
            title={choice.title}
            disabled={disabled}
            className={`${styles.choice} ${choice.tone ? `${toneClass} ${styles.toned}` : ''} ${choice.isQuiet ? styles.muted : ''} ${isSelected ? styles.selected : ''}`}
            data-tone={choice.tone}
            onClick={() => onChange(isSelected ? null : choice.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {choice.label}
          </button>
        );
      })}
    </div>
  );
}
